import type { Label } from '@libs/src/models/label.model';
import type {
  MatchConfigCustomSegmentConfig,
  MatchSegment,
} from '@libs/src/models/match.interfaces';
import type { PathwayPopulated } from '@libs/src/models/pathway.interface';
import type {
  ProgramInvitationEmail,
  programTypes,
} from '@libs/src/models/program.model';
import type { Survey } from '@libs/src/models/survey.model';
import { PlatformProgramTemplate } from '@libs/src/platform-config/platform-program-template';
import type { PlatformSurveyTemplate } from '@libs/src/platform-config/platform-survey-template';

export const programPhaseTypeFeedback = 'feedback';
export const programPhaseTypeMatchmaking = 'matchmaking';
export const programPhaseTypeSummary = 'summary';
export const programPhaseTypeTouchpoint = 'touchpoint';

export interface BaseProgramTemplatePhase {
  _id?: string;
  description?: string;
  email?: { body: string; subject: string };
  hidden_surveys?: Array<PlatformSurveyTemplate | Survey | string>;
  name?: string;
  phase_completed?: never;
  schedule?: {
    date?: Date;
    daysFromProgramStart?: number;
    time?: string;
  };
  surveys?: Array<PlatformSurveyTemplate | Survey | string>;
}

export interface FeedbackProgramTemplatePhase extends BaseProgramTemplatePhase {
  type: typeof programPhaseTypeFeedback;
}

export interface MatchmakingProgramTemplatePhase
  extends BaseProgramTemplatePhase {
  custom_segment_configs?: MatchConfigCustomSegmentConfig[];
  match_generation_completed?: boolean;
  match_pairings_generation_completed?: boolean;
  match_segment_level?: 'preferred' | 'required';
  match_segments?: MatchSegment[];
  type: typeof programPhaseTypeMatchmaking;
}

export interface SummaryProgramTemplatePhase extends BaseProgramTemplatePhase {
  type?: typeof programPhaseTypeSummary;
}

export interface TouchpointProgramTemplatePhase
  extends BaseProgramTemplatePhase {
  skills?: Array<{ display_text: string; name: string }>;
  thumbnail?: {
    imageAlt?: string;
    imageUrl?: string;
    videoUrl?: string;
  };
  type: typeof programPhaseTypeTouchpoint;
}

export type ProgramTemplatePhase =
  | FeedbackProgramTemplatePhase
  | MatchmakingProgramTemplatePhase
  | SummaryProgramTemplatePhase
  | TouchpointProgramTemplatePhase;

export const ProgramTemplateStatuses = {
  active: 'active',
  deleted: 'deleted',
} as const;

export interface ProgramTemplate {
  _id?: string;
  cover_photo?: {
    url?: string;
  };
  description?: string;
  end_date?: Date;
  features_enabled?: {
    group_communications?: boolean;
  };
  group?: string;
  invitation_email?: ProgramInvitationEmail;
  labels?: Label[];
  max_match_capacity?: number;
  name?: string;
  pathways?: Array<PathwayPopulated['_id']>;
  phases?: ProgramTemplatePhase[];
  platform_config?: PlatformProgramTemplate['_id'];
  program_config?: {
    allow_both_primary_and_secondary?: boolean;
    primary_name?: string;
    secondary_name?: string;
  };
  program_type?:
    | typeof programTypes.alwaysOn
    | typeof programTypes.repeat
    | typeof programTypes.single;
  setup_checklist?: never;
  slug?: string;
  start_date?: Date;
  status?:
    | typeof ProgramTemplateStatuses.active
    | typeof ProgramTemplateStatuses.deleted;
  updated_at?: Date;
}
